import { useEffect, useState } from 'react';
import { AppDispatch } from '../core/data/redux/store';
import { useDispatch, useSelector } from 'react-redux';
import { createSpouse } from '../core/data/redux/employee/employeeAction';
import { message } from 'antd';
import dayjs from 'dayjs';

interface SpouseFormValues {
  spouseName: string;
  spouseDateOfBirth: Date | null;
  spouseProfession: string;
  spouseDescription: string;
  spouseFullAddress: string;
  spouseAnniversaryDate: Date | null;
  spousePhone: string;
  spouseEmail: string;
  spouseNationality: string;
  spouseEmployer: string;
}

export const usePartnerForm = () => {
  const { employeeFullDetails } = useSelector((state: any) => state.employee);
  const [spouseData, setSpouseData] = useState<SpouseFormValues>({
    spouseName: '',
    spouseDateOfBirth: null,
    spouseProfession: '',
    spouseDescription: '',
    spouseFullAddress: '',
    spouseAnniversaryDate: null,
    spousePhone: '',
    spouseEmail: '',
    spouseNationality: '',
    spouseEmployer: ''
  });

  const [spouseErrors, setSpouseErrors] = useState<{ [key: string]: string }>({});
  const { employee } = useSelector((state: any) => state.employee);

  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    if (employeeFullDetails && employeeFullDetails.employee.length > 0) {
      const spouse = employeeFullDetails?.employee[0];
      setSpouseData({
        spouseName: spouse?.SpouseName || '',
        spouseDateOfBirth: spouse?.SpouseDateOfBirth ? new Date(spouse.SpouseDateOfBirth) : null,
        spouseProfession: spouse?.SpouseProfession || '',
        spouseDescription: spouse?.SpouseDescription || '',
        spouseFullAddress: spouse?.SpouseFullAddress || '',
        spouseAnniversaryDate: spouse?.SpouseAnniversaryDate ? new Date(spouse.SpouseAnniversaryDate) : null,
        spousePhone: spouse?.SpousePhone || '',
        spouseEmail: spouse?.SpouseEmail || '',
        spouseNationality: spouse?.SpouseNationality || '',
        spouseEmployer: spouse?.SpouseEmployer || ''
      });
    }
  }, [employeeFullDetails]);

  const handleSpouseChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setSpouseData(prevData => ({ ...prevData, [name]: value }));
  };
  const resetSpouseForm = () => {
    setSpouseData({
      spouseName: '',
      spouseDateOfBirth: null,
      spouseProfession: '',
      spouseDescription: '',
      spouseFullAddress: '',
      spouseAnniversaryDate: null,
      spousePhone: '',
      spouseEmail: '',
      spouseNationality: '',
      spouseEmployer: ''
    });
    setSpouseErrors({});
  };

  const editingSections = useSelector((state: any) => state.employee.editingSections);
  const modalType = useSelector((state: any) => state.employee.modalType);

  const validateSpouse = async (): Promise<{ isValid: boolean; missingFields?: string[] }> => {
    let tempErrors: { [key: string]: string } = {};
    let isValid = true;
    const missingFields: string[] = [];

    const phoneRegex = /^[6-9]\d{9}$/;

    // Required fields validation
    if (!spouseData.spouseName) {
      tempErrors.spouseName = 'Spouse name is required';
      missingFields.push("Name");
      isValid = false;
    }
    if (!spouseData.spouseDateOfBirth) {
      tempErrors.spouseDateOfBirth = 'Date of birth is required';
      missingFields.push("Date of Birth");
      isValid = false;
    }
    if (spouseData.spouseEmail && !/\S+@\S+\.\S+/.test(spouseData.spouseEmail)) {
      tempErrors.spouseEmail = 'Email is not valid';
      missingFields.push("Invalid Email");
      isValid = false;
    }
    if (spouseData.spousePhone && !phoneRegex.test(spouseData.spousePhone)) {
      tempErrors.spousePhone = 'Phone number is not valid';
      missingFields.push("Invalid Phone Number");
      isValid = false;
    }

    setSpouseErrors(tempErrors);

    return { isValid, missingFields };
  };

  const handleSpouseSubmit = (e: React.FormEvent, clearForm: boolean = false, showMessage: boolean = true): Promise<boolean> => {
    return new Promise((resolve) => {
      e.preventDefault();

      const missingFields: string[] = [];
      if (!spouseData.spouseName || !spouseData.spouseDateOfBirth) {
        missingFields.push("Spouse"); 
      }

      if (missingFields.length > 0) {
        if (showMessage) {
          message.warning(`Please fill all fields to add/update a ${missingFields.join(", ")}`);
        }
        resolve(false);
        return;
      }

      if (!validateSpouse()) {
        resolve(false);
        return;
      }
      const spousePayload = {
        spouseName: spouseData.spouseName,
        spouseDateOfBirth: spouseData.spouseDateOfBirth ? spouseData.spouseDateOfBirth.toISOString().split('T')[0] : null,
        spouseProfession: spouseData.spouseProfession,
        spouseDescription: spouseData.spouseDescription,
        spouseFullAddress: spouseData.spouseFullAddress,
        spouseAnniversaryDate: spouseData.spouseAnniversaryDate ? dayjs(spouseData.spouseAnniversaryDate).format('YYYY-MM-DD') : null,
        spousePhone: spouseData.spousePhone,
        spouseEmail: spouseData.spouseEmail,
        spouseNationality: spouseData.spouseNationality,
        spouseEmployer: spouseData.spouseEmployer
      };
      const employeeId = employee?.EmployeeId || employeeFullDetails?.employee[0].EmployeeID;
      // Dispatching the action with employeeId and the spouse data
      const result = dispatch(createSpouse({ employeeId: employeeId, spouseData: spousePayload }))
      result.then((response: any) => {
        if (response.meta.requestStatus === "fulfilled") {
          if (showMessage) message.success("Spouse saved successfully")
          if (clearForm) resetSpouseForm();
          resolve(true);
        } else if (response.meta.requestStatus === "rejected") {
          if (showMessage) message.error(response.payload)
          resolve(false);
        } else {
          if (showMessage) message.error("something went wrong")
          resolve(false);
        }
      }).catch((error: any) => {
        if (showMessage) message.error("Failed to save spouse");
        resolve(false);
      });
    });
  };


  return {
    spouseData,
    setSpouseData,
    handleSpouseChange,
    handleSpouseSubmit,
    spouseErrors,
    resetSpouseForm,
    validateSpouse
  };
};
